import { useEffect, useState } from "react";
import { MonitorPlay } from "lucide-react";
import { api } from "../../lib/api";

// Visual Modeling Tracker: how many minutes of the real-child video models
// each child watched this week, against the suggested clinical goal of
// 5 minutes per week. Watch time only records with progress consent, so a
// quiet zero here can simply mean tracking is off for that child.
export const VideoModelingTracker = () => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/children").then(async ({ data }) => {
      const kids = data.filter((c) => c.status === "active");
      const results = await Promise.all(kids.map((c) =>
        api.get(`/speech/children/${c.child_id}/video-modeling`)
          .then((r) => r.data).catch(() => null)));
      setRows(results.filter(Boolean));
    }).catch(() => setRows([]));
  }, []);

  if (!rows || rows.length === 0) return null;
  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="video-modeling-tracker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base md:text-lg font-bold font-display flex items-center gap-2">
          <MonitorPlay className="h-5 w-5 text-secondary" aria-hidden="true" />
          Video modeling this week
        </h3>
        <p className="text-xs text-muted-foreground">
          Suggested goal: 5 minutes per week of watching the mouth models
        </p>
      </div>
      <div className="space-y-3">
        {rows.map((r) => {
          const pct = Math.min(100, Math.round((r.seconds_this_week / (r.goal_minutes * 60)) * 100));
          return (
            <div key={r.child_id} data-testid={`video-modeling-row-${r.child_id}`}>
              <div className="flex items-center justify-between text-sm font-semibold mb-1">
                <span>{r.display_name}</span>
                <span data-testid={`video-modeling-minutes-${r.child_id}`}
                  className={r.goal_met ? "text-emerald-600" : "text-muted-foreground"}>
                  {r.minutes_this_week} / {r.goal_minutes} min
                  {r.goal_met && " — goal met!"}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-muted overflow-hidden"
                role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                aria-label={`${r.display_name} watched ${r.minutes_this_week} of ${r.goal_minutes} suggested minutes`}>
                <div className={`h-full rounded-full transition-all ${r.goal_met ? "bg-emerald-500" : "bg-sky-500"}`}
                  style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
